import cx from 'classnames';
import {useCallback, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {useRequestLogin} from '~/common/fabric/Card/components/LoginModal/hooks/useRequestLogin';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {subscribeToUserStateChanges, type UserData} from '~/common/utils/userStateSync';
import {fetchAllFavoritesThunk} from '~/GalleryPB/api/favorites';
import {Button} from '~library/atoms/Button';
import {userLoggedIn} from '~library/organisms/LoginModal/reducer/actions';
import {FavoritesFillIcon} from '~library/svg/FavoritesFillIcon';
import {FavoritesLineIcon} from '~library/svg/FavoritesLineIcon';
import {FavoriteHeartIcon} from '~library/svg/v2/FavoriteHeartIcon';
import {cloud, roseWebText} from '~libSass/base/_exports.sass';

type FavoritesHeartProps = {
  identifier: string;
  onClick?: () => void;
  favorited?: boolean;
  ratio?: number;
  variant?: boolean;
};

export const FavoritesHeart = ({
  identifier,
  onClick,
  favorited,
  ratio,
  variant,
}: FavoritesHeartProps) => {
  const isLargishAndUp = useIsLargishAndUp();
  const dispatch = useDispatch();
  const iconRatio = ratio || (isLargishAndUp ? 0.75 : 0.5);
  const {loggedIn, requestLogin} = useRequestLogin();

  // Subscribe to user state changes from other stores
  useEffect(() => {
    return subscribeToUserStateChanges((userData: UserData) => {
      if (userData?.userId) {
        dispatch(
          userLoggedIn(
            userData.userId,
            userData.userName,
            userData.subscriptionPlanName,
            userData.email,
            userData.avatarDisk,
            userData.imgUrl,
            userData.initials,
            userData.firstName
          )
        );
      }
    });
  }, [dispatch]);

  const onClickHandler = useCallback(async () => {
    let successfulLogin = false;

    if (!loggedIn) {
      successfulLogin = await requestLogin();
    }

    if ((successfulLogin || loggedIn) && typeof onClick === 'function') {
      // @ts-ignore
      await dispatch(fetchAllFavoritesThunk);
      onClick();
    }
  }, [loggedIn, requestLogin, dispatch, onClick]);

  const icon = useMemo(() => {
    if (variant) {
      return <FavoriteHeartIcon />;
    }

    return favorited ? (
      <FavoritesFillIcon ratio={iconRatio} color={roseWebText} />
    ) : (
      <FavoritesLineIcon ratio={iconRatio} color={cloud} />
    );
  }, [iconRatio, favorited, variant]);

  return (
    <Button
      className={cx('gallery-template_favorite-btn', {
        'gallery-template_favorite-btn--variant': variant,
        'gallery-template_favorite-btn--variant--selected': variant && favorited,
        'gallery-template_favorite-btn--selected': favorited,
      })}
      variant="transparent"
      size="small"
      data-qa-id={`button-toggle-favorite_${identifier}-${favorited ? 'selected' : ''}`}
      onClick={onClickHandler}
      override
      disableProcessing={variant}
    >
      {icon}
    </Button>
  );
};
